import Phaser from 'phaser';
import { DRUID } from '../../config';
import { Enemy } from '../Enemy';
import { Projectile } from '../Projectile';
import { BuildingSlot, ChainUpgradeEffect } from '../../types';
import { WALL_X, WALL_WIDTH, ENEMY_MIN_Y, ENEMY_MAX_Y } from '../../config';

interface Bramble {
    x: number;      // left edge of zone
    y: number;      // top edge of zone
    width: number;
    height: number;
    hp: number;
    maxHP: number;
    timer: number;
    dps: number;
    slowEffect: number;
    graphics: Phaser.GameObjects.Graphics;
    // Cached random positions for jungle vine drawing (generated once on creation)
    vineDetails: { rx: number; ry: number; size: number; angle: number; type: 'vine' | 'thorn' }[];
}

export class Druid {
    scene: Phaser.Scene;
    x: number;
    y: number;
    level: number;
    position: BuildingSlot;
    unitType: string = 'druid';
    graphics: Phaser.GameObjects.Graphics;
    sprite: Phaser.GameObjects.Sprite;
    private castTimer: number = 0;
    private projectiles: Projectile[];
    stackCount: number = 1;

    // Chain upgrade bonuses
    bonusDamageMult: number = 1;
    attackIntervalMult: number = 1;
    bonusSlowEffect: number = 0;
    bonusSizeMult: number = 0;
    globalDamageMult: number = 1;
    attackSpeedMult: number = 1;

    // Active brambles
    brambles: Bramble[] = [];

    constructor(
        scene: Phaser.Scene,
        x: number,
        y: number,
        position: BuildingSlot,
        projectiles: Projectile[]
    ) {
        this.scene = scene;
        this.x = x;
        this.y = y;
        this.level = 1;
        this.position = position;
        this.graphics = scene.add.graphics();
        this.sprite = scene.add.sprite(x, y, 'building_druid');
        this.sprite.setOrigin(0.5, 110 / 128);
        this.projectiles = projectiles;
        this.stackCount = 1;
        this.draw();
    }

    getBrambleDps(): number {
        return DRUID.baseDamage * DRUID.brambleDpsPercent * this.stackCount * this.bonusDamageMult * this.globalDamageMult;
    }

    getBrambleHP(): number {
        return DRUID.baseDamage * DRUID.brambleHpFactor * (1 + (this.bonusDamageMult - 1) * 0.5);
    }

    getSlowEffect(): number {
        return Math.max(0.1, DRUID.brambleSlowEffect - this.bonusSlowEffect);
    }

    getBrambleWidth(): number {
        return DRUID.brambleWidth * (1 + this.bonusSizeMult);
    }

    getBrambleHeight(): number {
        return DRUID.brambleHeight;
    }

    addStack(): void {
        this.stackCount++;
    }

    applyChainUpgrade(effect: ChainUpgradeEffect): void {
        if (effect.damageMultBonus !== undefined) this.bonusDamageMult *= (1 + effect.damageMultBonus);
        if (effect.attackIntervalMult !== undefined) this.attackIntervalMult *= effect.attackIntervalMult;
        if (effect.slowEffectBonus !== undefined) this.bonusSlowEffect += effect.slowEffectBonus;
        if (effect.brambleSizeMult !== undefined) this.bonusSizeMult += effect.brambleSizeMult;
        this.level++;
        this.draw();
    }

    update(time: number, delta: number, enemies: Enemy[]): void {
        // Update existing brambles
        for (let i = this.brambles.length - 1; i >= 0; i--) {
            const bramble = this.brambles[i];
            bramble.timer -= delta;

            // Brambles block, damage and slow enemies
            for (const enemy of enemies) {
                if (enemy.isDead) continue;
                if (enemy.isBurrowed) continue;

                // Block enemies at the right edge of the bramble (like a wall)
                const inYBand = enemy.y >= bramble.y && enemy.y <= bramble.y + bramble.height;
                if (inYBand && !enemy.isAttacking && enemy.x <= bramble.x + bramble.width + 20 && enemy.x > bramble.x) {
                    // Enemy reached the bramble — stop and attack it
                    enemy.x = bramble.x + bramble.width + 20;
                    enemy.isAttacking = true;
                }

                // Enemies stopped at bramble attack it (deal damage to bramble)
                if (inYBand && enemy.isAttacking && enemy.x <= bramble.x + bramble.width + 25 && enemy.x >= bramble.x) {
                    bramble.hp -= enemy.damage * delta / 1000;
                }

                // Enemies inside the zone take DPS and slow
                if (enemy.x >= bramble.x && enemy.x <= bramble.x + bramble.width &&
                    inYBand) {
                    const dmg = bramble.dps * delta / 1000;
                    enemy.takeDamage(dmg);
                    enemy.applySlow(bramble.slowEffect, 500);
                }
            }

            // Remove expired or destroyed brambles
            if (bramble.timer <= 0 || bramble.hp <= 0) {
                // Release enemies that were blocked by this bramble
                for (const enemy of enemies) {
                    if (enemy.isDead) continue;
                    if (enemy.isAttacking &&
                        enemy.x >= bramble.x && enemy.x <= bramble.x + bramble.width + 25 &&
                        enemy.y >= bramble.y && enemy.y <= bramble.y + bramble.height) {
                        enemy.isAttacking = false;
                    }
                }
                bramble.graphics.destroy();
                this.brambles.splice(i, 1);
            }
        }

        // Cast new brambles
        this.castTimer -= delta;
        if (this.castTimer <= 0 && this.brambles.length < DRUID.maxBrambles) {
            if (enemies.some(e => !e.isDead)) {
                this.castTimer = DRUID.attackInterval * this.attackIntervalMult / this.attackSpeedMult;
                this.placeBramble(enemies);
            }
        }

        this.draw();
        this.drawBrambles();
    }

    private placeBramble(enemies: Enemy[]): void {
        const wallEdge = WALL_X + WALL_WIDTH;
        let nearestEnemy: Enemy | null = null;
        let minDist = Infinity;
        for (const enemy of enemies) {
            if (enemy.isDead) continue;
            if (enemy.enemyType === 'boulder') continue;
            if (enemy.isBurrowed) continue;
            if (enemy.isGoblin) continue;
            const dist = enemy.x - wallEdge;
            if (dist > 0 && dist < DRUID.range && dist < minDist) {
                minDist = dist;
                nearestEnemy = enemy;
            }
        }

        if (!nearestEnemy) return;

        const bWidth = this.getBrambleWidth();
        const bHeight = this.getBrambleHeight();

        // Place centered on nearest enemy
        const bx = Math.max(wallEdge + 20, nearestEnemy.x - bWidth / 2);
        const by = Math.max(ENEMY_MIN_Y, Math.min(nearestEnemy.y - bHeight / 2, ENEMY_MAX_Y - bHeight));

        // Generate dense vine + thorn details (cached for perf)
        const vineDetails: Bramble['vineDetails'] = [];
        const detailCount = Math.floor(bWidth * bHeight / 120);
        for (let d = 0; d < detailCount; d++) {
            vineDetails.push({
                rx: Math.random() * bWidth,
                ry: Math.random() * bHeight,
                size: 4 + Math.random() * 10,
                angle: Math.random() * Math.PI * 2,
                type: Math.random() < 0.6 ? 'vine' : 'thorn',
            });
        }

        const bramble: Bramble = {
            x: bx,
            y: by,
            width: bWidth,
            height: bHeight,
            hp: this.getBrambleHP(),
            maxHP: this.getBrambleHP(),
            timer: DRUID.brambleDuration,
            dps: this.getBrambleDps(),
            slowEffect: this.getSlowEffect(),
            graphics: this.scene.add.graphics(),
            vineDetails,
        };

        this.brambles.push(bramble);
    }

    private drawBrambles(): void {
        for (const bramble of this.brambles) {
            const gfx = bramble.graphics;
            gfx.clear();

            const hpPct = bramble.hp / bramble.maxHP;
            const fadeIn = Math.min(1, bramble.timer / 2000);
            const alpha = fadeIn * Math.max(0.3, hpPct);
            const cx = bramble.x + bramble.width / 2;
            const cy = bramble.y + bramble.height / 2;

            // Tangled vine strands running through the patch
            for (const detail of bramble.vineDetails) {
                const dx = bramble.x + detail.rx;
                const dy = bramble.y + detail.ry;

                if (detail.type === 'vine') {
                    // Thick green vine strand
                    const len = detail.size * 1.8;
                    const a = detail.angle;
                    gfx.lineStyle(2 + detail.size * 0.15, 0x2a5e16, 0.7 * alpha);
                    gfx.beginPath();
                    gfx.moveTo(dx, dy);
                    // Wavy middle point
                    const mx = dx + Math.cos(a) * len * 0.5 - Math.sin(a) * 5;
                    const my = dy + Math.sin(a) * len * 0.5 + Math.cos(a) * 5;
                    gfx.lineTo(mx, my);
                    const ex = dx + Math.cos(a) * len;
                    const ey = dy + Math.sin(a) * len;
                    gfx.lineTo(ex, ey);
                    gfx.strokePath();

                    // Small leaf cluster at end
                    gfx.fillStyle(0x3a7a28, 0.6 * alpha);
                    gfx.fillCircle(ex, ey, 2.5 + detail.size * 0.15);

                } else {
                    // Thorn/prick — short sharp spike
                    const len = detail.size * 0.8;
                    const a = detail.angle;
                    // Brown woody base
                    gfx.lineStyle(1.5, 0x6b4226, 0.75 * alpha);
                    gfx.beginPath();
                    gfx.moveTo(dx, dy);
                    const tx = dx + Math.cos(a) * len;
                    const ty = dy + Math.sin(a) * len;
                    gfx.lineTo(tx, ty);
                    gfx.strokePath();
                    // Sharp light tip
                    gfx.fillStyle(0xc8a060, 0.8 * alpha);
                    gfx.fillCircle(tx, ty, 1.2);
                }
            }

            // Overlay: a few thick main vine trunks running across
            const trunkCount = Math.max(2, Math.floor(bramble.height / 35));
            for (let t = 0; t < trunkCount; t++) {
                const ty = bramble.y + (t + 0.5) * bramble.height / trunkCount;
                const phase = t * 2.7;
                gfx.lineStyle(3, 0x1f5010, 0.6 * alpha);
                gfx.beginPath();
                gfx.moveTo(bramble.x - 3, ty);
                for (let vx = bramble.x; vx <= bramble.x + bramble.width + 3; vx += 6) {
                    gfx.lineTo(vx, ty + Math.sin(vx * 0.08 + phase) * 6);
                }
                gfx.strokePath();
            }

            // A couple vertical trunks
            const vTrunkCount = Math.max(1, Math.floor(bramble.width / 30));
            for (let t = 0; t < vTrunkCount; t++) {
                const tx = bramble.x + (t + 0.5) * bramble.width / vTrunkCount;
                const phase = t * 3.1;
                gfx.lineStyle(2.5, 0x1a4a0e, 0.5 * alpha);
                gfx.beginPath();
                gfx.moveTo(tx, bramble.y - 3);
                for (let vy = bramble.y; vy <= bramble.y + bramble.height + 3; vy += 6) {
                    gfx.lineTo(tx + Math.sin(vy * 0.07 + phase) * 5, vy);
                }
                gfx.strokePath();
            }

            // Scattered leaf clusters for density
            for (let i = 0; i < 8; i++) {
                // Use deterministic positions based on bramble coords
                const lx = bramble.x + ((i * 37 + 13) % bramble.width);
                const ly = bramble.y + ((i * 53 + 7) % bramble.height);
                const r = 4 + (i % 3) * 2;
                gfx.fillStyle(0x2d6a1e, 0.5 * alpha);
                gfx.fillCircle(lx, ly, r);
                gfx.fillStyle(0x3d8a2e, 0.35 * alpha);
                gfx.fillCircle(lx + 2, ly - 1, r * 0.7);
            }

            // HP bar at top center
            const barW = Math.min(50, bramble.width * 0.7);
            const barH = 4;
            const barX = cx - barW / 2;
            const barY = bramble.y - 8;
            gfx.fillStyle(0x222222, 0.8);
            gfx.fillRect(barX, barY, barW, barH);
            gfx.fillStyle(hpPct > 0.5 ? 0x33bb33 : 0xcc8833, 0.9);
            gfx.fillRect(barX, barY, barW * hpPct, barH);
        }
    }

    draw(): void {
        this.sprite.setTexture('building_druid');
        this.graphics.clear();

        if (this.level > 1) {
            this.graphics.fillStyle(0x44cc44, 1);
            for (let i = 0; i < Math.min(this.level - 1, 5); i++) {
                this.graphics.fillCircle(this.x - 6 + i * 3, this.y + 8, 1.5);
            }
        }
    }

    destroy(): void {
        this.graphics.destroy();
        this.sprite.destroy();
        for (const bramble of this.brambles) {
            bramble.graphics.destroy();
        }
        this.brambles.length = 0;
    }
}
